import { usePageProps, withLayouts } from 'react-dx'
import { useLingui } from '@lingui/react/macro'

import RootLayout from '../_layout/layouts/RootLayout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import SiteSpecificContextLayout from '../_layout/layouts/SiteSpecificContextLayout'
import { paddingHeaderHeightCls } from '../_layout/Header/constants'
import LayoutsDefaultLayout from '../_layout'
import MusicList from '../_components/MusicList'
import { PageStoreProvider } from '../_shared/formStore'

import { createMusicFormStore } from './_store/formStore'
import { fluentFlexItemClassName } from './classes'
import GeneratorForm from './_blocks/GeneratorForm'

import type { MusicListActionRef } from '../_components/MusicList'
import type { InferGetServerSidePropsType } from 'next'
import type { TNextPagePro } from '@/types/global'

import { getServerSideData } from '@/helpers/server-data'
import { FluentFlex, FluentFlexRoot } from '@/components/FluentFlex'
import { getTadFeatureData } from '@/pages/tad.ai/_services/feature-service'
import { FeatureSeoPage } from '@/pages/tad.ai/_blocks/FeatureSeoPage'

export const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const {
    seoPageData: { hero },
  } = props
  const { t } = useLingui()
  const musicListRef = useRef<MusicListActionRef>(null)
  const { collapsed } = useGlobalAudioPlayer()

  return (
    <PageStoreProvider createStore={createMusicFormStore}>
      <FluentFlexRoot itemClassName={fluentFlexItemClassName}>
        <FluentFlex
          className={`min-h-screen flex-col xl:h-screen ${paddingHeaderHeightCls}`}
        >
          <h1
            id='top'
            className='mb-3 ml-8 text-xl font-bold md:mb-0 md:text-3xl md:leading-[64px]'
          >
            {hero?.h1 || t`AI Music Generator`}
          </h1>
          <FluentFlex
            className={cls`block flex-col xl:flex-row ${!collapsed && 'pb-32 xl:pb-24'}`}
          >
            <GeneratorForm
              onCreated={() => {
                musicListRef.current?.refetchIfPage1(true)
              }}
            />
            <MusicList
              ref={musicListRef}
              className={cls`mx-auto h-full md:!px-8 xl:max-w-none`}
            />
          </FluentFlex>
        </FluentFlex>
      </FluentFlexRoot>
    </PageStoreProvider>
  )
}

Page.noPageKey = true

const LayoutsPage = withLayouts(
  Page,
  [
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()

      return (
        <LayoutsDefaultLayout
          {...props}
          main={{
            className: cls`bg-f-other-1`,
            includingHeader: true,
          }}
          sideBar={{
            className: '',
          }}
        >
          {seoPageData.showFunctionArea ? props.children : null}
          <FeatureSeoPage pageData={seoPageData} />
        </LayoutsDefaultLayout>
      )
    },
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()
      const { seoTitle } = useConfig()

      return (
        <RootLayout
          seo={{
            title: seoTitle(seoPageData.title),
            description: seoPageData.description,
          }}
          {...props}
        />
      )
    },
    (props) => {
      return <SiteSpecificContextLayout {...props} />
    },
    AntdLayout,
  ],
  {
    propertiesHoist: ['noPageKey'],
  },
)

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (hostInfo, ctx, helpers) => {
  const trpcHelpers = await helpers.getTrpcHelpers()
  const locale = ctx?.locale || 'en'
  const url = hostInfo.normalizeUrl().url.pathname

  const [, seoPageData] = await Promise.all([
    trpcHelpers.preset.labelsTree.prefetch({ language: locale }),
    getTadFeatureData(url, locale),
  ])

  return {
    seoPageData,
    trpcState: trpcHelpers.dehydrate(),
  }
})
